'use strict';
import { v1 as uuidv1 } from 'uuid';
import moment from 'moment';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import EcsElement from '@/components/ecs-element/EcsElement';
import { CONTACT_TYPE } from '@/constants/contactConstants';
import { INTERACTION_STATE } from '@/constants/interaction/constants';
import { NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID, OUTGOING_EMAIL } from '@/constants/socket-events';
import { ELEMENT_STATE } from '@/constants/element/constants';
import ContactService from '@/services/contactService';
import InteractionService from '@/services/interactionService';
import { openExternalLinks } from '@/utils';
import { agentSubscriptions } from '@/constants/kpi-counters/subscriptions';
import deserializeInteraction from '@/store/helpers/interactionDTODeserialization.ts';

const removeAfterHandling = {};
const answeredInteractions = {};
const postponedInteractions = {};

const socketEventActions = {
  SOCKET_EVENT_INTERACTION_CHANGED_EVENT({ dispatch, commit, getters }, event) {
    const partialInteraction = deserializeInteraction(event.serializedData.Interaction);

    const updateInteraction = () => {
      const activeInteraction = getters.getActiveInteraction(partialInteraction.interactionId);
      const pendingInteraction = getters.getPendingInteraction(partialInteraction.interactionId);

      if (activeInteraction) {
        commit('updateActiveInteraction', partialInteraction);
      } else if (pendingInteraction) {
        commit('updatePendingInteraction', partialInteraction);
      } else {
        // Interaction is not in active or pending state.
      }
    };

    updateInteraction();
  },

  SOCKET_EVENT_INTERACTION_ACCEPTED_EVENT({ dispatch, commit, getters, rootGetters }, event) {
    const interaction = new EcsInteraction(event);

    if (answeredInteractions[interaction.interactionId]) {
      // Already answered: When using WebRTC softPhone, ECS is sending this event twice.
      return;
    }

    if (!postponedInteractions[interaction.interactionId]) {
      answeredInteractions[interaction.interactionId] = true;
    }

    if (interaction.completedByDelegatingToSupervisor) {
      return dispatch('removePendingInteraction', interaction);
    }

    if (interaction.isEmail) {
      dispatch('INTERACTIONS/emails/addEmailData', { event, signature: '' }, { root: true });
    }

    const duplicatedHistoricalCallEntry = getters.getHistoricalInteraction(event.interactionId);
    if (duplicatedHistoricalCallEntry) {
      dispatch('ELEMENTS/removeClosedElementsForInteraction', { interactionId: event.interactionId }, { root: true });
      commit('deleteHistoricalInteraction', event.interactionId);
    }

    const pendingInteraction = getters.getPendingInteraction(event.interactionId);
    interaction.interactionVariables = pendingInteraction ? pendingInteraction.interactionVariables : null;
    dispatch('movePendingInteractionToActive', interaction);

    const element = new EcsElement(event);

    if (interaction.isInternal) {
      const targetEndPoint =
        rootGetters['AGENT/loggedInAgent'].endPoint === event.origin ? event.destination : event.origin;
      const { id, contactType, name, email, endPoint } = rootGetters['CONTACTS/getInternalBusinessContactByEndPoint'](
        targetEndPoint
      );
      element.receptor = { type: contactType, id, name, email, endPoint };
    } else if (pendingInteraction && pendingInteraction.isConsultReceiver) {
      const { id, contactType, name, email, endPoint } = rootGetters['CONTACTS/getInternalBusinessContact'](
        pendingInteraction.consult.requestor.id
      );
      element.receptor = { type: contactType, id, name, email, endPoint };
    } else {
      const name = interaction.formattedCustomerFullName;
      const email = interaction.customerEmail;
      element.receptor = { type: CONTACT_TYPE.crm, id: event.contactId, name, email };
    }

    const recordingProfile = rootGetters['AGENT/loggedInAgent'].callRecording;

    if (interaction.isTelephony && recordingProfile) {
      element.recording = {
        isEnabled: recordingProfile.recordingOption.toUpperCase().includes('ALWAYS'),
        isPaused: false
      };
    }

    // Remove this conditional wrapper when AIR-6908 is resolved (inconsistent elementId)
    if (event.handlingType !== 'CallBack') {
      dispatch('ELEMENTS/addElement', element, { root: true });
    }

    openExternalLinks(interaction.automaticPopupLinks);
  },

  SOCKET_EVENT_INTERACTION_ANSWERED_EVENT({ dispatch, getters }, event) {
    const interaction = new EcsInteraction(event);

    const pendingInteraction = getters.getPendingInteraction(interaction.interactionId);
    if (pendingInteraction) {
      dispatch('movePendingInteractionToActive', interaction);
    } else {
      dispatch('editActiveInteraction', interaction);
    }
  },

  SOCKET_EVENT_ATTENDED_INTERACTION_TRANSFERRED_TO_AGENT_EVENT({ commit, dispatch, rootGetters }, event) {
    if (event.agentId !== rootGetters['AGENT/loggedInAgent'].id) {
      // Transferor side
      dispatch('removeActiveInteraction', event);
    } else {
      // Receiver side
      const interaction = new EcsInteraction(event);
      const element = rootGetters['ELEMENTS/getElement'](event.elementId);
      interaction.consult = { requestor: null, receiver: null };

      if (element) {
        const name = interaction.formattedCustomerFullName;
        const email = interaction.customerEmail;
        element.receptor = { type: CONTACT_TYPE.crm, id: event.contactId, name, email };
        commit('ELEMENTS/updateElement', element, { root: true });
      }

      commit('updateActiveInteraction', interaction);
    }
  },

  SOCKET_EVENT_INTERACTION_ABANDONED_EVENT({ dispatch }, event) {},

  SOCKET_EVENT_INTERACTION_CANCELLED_EVENT({ dispatch }, event) {
    // TODO: When internal telephony is cancelled ECS still have
    // a wrap-up time. Therefore, we don't need to remove the interaction yet
    // const interaction = new EcsInteraction(event);
    // dispatch('removeActiveInteraction', interaction);
    const interaction = new EcsInteraction(event);
    if (interaction.isEmail) {
      dispatch('INTERACTIONS/emails/moveEmailToHistory', event, { root: true });
    }
  },

  SOCKET_EVENT_INTERACTION_CONFERENCE_STARTED_EVENT({ dispatch, getters, commit, rootGetters }, event) {
    const interaction = new EcsInteraction(event);

    // NOTE: Patch to solve incorrect data for the interaction
    // and missing data for the element from ECS for the consult receiver
    const activeInteraction = getters.getActiveInteraction(event.interactionId);
    if (activeInteraction.isConsultReceiver) {
      const element = rootGetters['ELEMENTS/getElementsForInteraction'](interaction).filter(el => !el.isEnded)[0];

      if (!element) {
        return;
      }

      element.elementStateId = ELEMENT_STATE.conferenced;
      element.elementStateName = 'Conferenced';
      dispatch('ELEMENTS/editElement', element, { root: true });

      if (interaction.isChat) {
        InteractionService.getAgentInteractionConversation(interaction.interactionId).then(response => {
          dispatch('CONVERSATIONS/setConversation', response.data, { root: true });
        });
      }
    }

    dispatch('editActiveInteraction', interaction);
  },

  SOCKET_EVENT_INTERACTION_CONFERENCE_STOPPED_EVENT({ dispatch, getters, rootGetters }, event) {
    const interaction = new EcsInteraction(event);

    // NOTE: Patch to solve incorrect data for the interaction
    // and missing data for the element from ECS for the consult receiver
    const activeInteraction = getters.getActiveInteraction(event.interactionId);
    if (activeInteraction.isConsultReceiver) {
      const element = rootGetters['ELEMENTS/getElementsForInteraction'](interaction).filter(el => !el.isEnded)[0];

      if (!element) {
        return;
      }

      element.elementStateId = ELEMENT_STATE.active;
      element.elementStateName = 'Active';
      dispatch('ELEMENTS/editElement', element, { root: true });

      interaction.interactionStateId = INTERACTION_STATE.consulting;
      interaction.interactionStateName = 'Consulting';
    }

    dispatch('editActiveInteraction', interaction);
  },

  SOCKET_EVENT_INTERACTION_CONSULT_REQUESTED_EVENT({ dispatch, rootGetters }, event) {
    const interaction = new EcsInteraction(event);
    const element = new EcsElement(event);

    // Set who is the consult.receiver
    const { consulteeId, consulteeName, consulteeTypeId, consulteeUri } = event;
    interaction.consult = {
      receiver: consulteeId ? { id: consulteeId, name: consulteeName, endPoint: consulteeUri } : null,
      requestor: null
    };

    const contact = rootGetters['CONTACTS/getContactByEndPoint'](consulteeUri);
    if (contact) {
      const { id, contactType, name, email } = contact;
      element.receptor = { type: contactType, id, name, email, endPoint: consulteeUri };
    } else {
      element.receptor = {
        type: consulteeTypeId,
        id: consulteeId,
        name: consulteeName,
        endPoint: consulteeUri
      };
    }

    dispatch('ELEMENTS/addElement', element, { root: true });

    dispatch('editActiveInteraction', interaction);
    if (interaction.isChat) {
      dispatch('WINDOWS/setFocusedElementId', element.elementId, { root: true });
    }
  },

  SOCKET_EVENT_INTERACTION_OFFERED_TO_AGENT_EVENT({ dispatch, getters, rootGetters }, event) {
    const interaction = new EcsInteraction(event);
    const element = new EcsElement(event);
    const contact = event.consultRequestorId
      ? rootGetters['CONTACTS/getInternalBusinessContact'](event.consultRequestorId)
      : null;

    const pendingDirectCall = getters.getPendingTelephonyInteractions.find(interaction => interaction.isDirectCall);
    if (pendingDirectCall && interaction.isTelephony) {
      dispatch('removePendingInteraction', pendingDirectCall);
    }

    // Set who is the consult.requestor
    const { consultRequestorId, consultRequestorName } = event;
    const requestor = consultRequestorId
      ? { id: consultRequestorId, name: consultRequestorName, endPoint: contact ? contact.endPoint : null }
      : null;
    interaction.consult = { receiver: null, requestor };

    //  const crmContact = rootGetters['CONTACTS/getContactV2'](interaction.crm.contactId);
    const loggedInAgent = rootGetters['AGENT/loggedInAgent'];

    // if (!crmContact && loggedInAgent.crm === 'Enabled')
    if (loggedInAgent.crm === 'Enabled') {
      dispatch('CONTACTS/requestCrmContact', interaction.crm.contactId, { root: true });
    }

    if (interaction.callbackRecordingFile || interaction.callbackRecordingTranscription) {
      const recording = {
        interactionId: interaction.interactionId,
        callbackRecordingFile: interaction.callbackRecordingFile,
        callbackRecordingTranscription: interaction.callbackRecordingTranscription
      };
      dispatch('INTERACTIONS/recordings/storeRecording', recording, { root: true });
    }

    if (event.elementId) {
      if (consultRequestorId) {
        const { id, name, email, endPoint } = contact;
        element.receptor = { type: CONTACT_TYPE.agent, id, name, email, endPoint };
      }

      dispatch('ELEMENTS/addElement', element, { root: true });
    }

    dispatch('upsertPendingInteraction', interaction);
  },

  SOCKET_EVENT_INTERACTION_CHANGED_HANDLER_EVENT({ dispatch }, event) {
    // Note: This may be only for Queue level event monitoring (dashboard/stats)
  },

  SOCKET_EVENT_INTERACTION_CREATED_OUTGOING_EVENT({ dispatch, getters, rootGetters }, event) {
    const interaction = new EcsInteraction(event);
    dispatch('addActiveInteraction', interaction);
    const element = new EcsElement(event);
    if (interaction.isOutgoingEmail) {
      element.elementStateId = ELEMENT_STATE.created;
      element.elementStateName = 'Created';
      element.receptor = {
        type: CONTACT_TYPE.unknown,
        id: '',
        name: 'New Email',
        email: ''
      };
      dispatch('ELEMENTS/addElement', element, { root: true });
    } else {
      const endPoint =
        rootGetters['AGENT/loggedInAgent'].endPoint === event.destination ? event.origin : event.destination;
      const contact =
        rootGetters['CONTACTS/getCrmContact'](interaction.crm.contactId) ||
        rootGetters['CONTACTS/getContactByEndPoint'](endPoint);
      if (contact) {
        const { id, contactType, name, email } = contact;
        element.receptor = { type: contactType, id, name, email, endPoint };
      } else {
        element.receptor = {
          type: interaction.crm.contactId ? CONTACT_TYPE.crm : CONTACT_TYPE.unknown,
          id: interaction.crm.contactId,
          name: interaction.crm.customerFullName || endPoint,
          endPoint
        };
      }
      const recordingProfile = rootGetters['AGENT/loggedInAgent'].callRecording;
      if (interaction.isTelephony && recordingProfile) {
        element.recording = {
          isEnabled: recordingProfile.recordingOption.toUpperCase().includes('ALWAYS'),
          isPaused: false
        };
      }
      dispatch('ELEMENTS/addElement', element, { root: true });
    }
  },

  SOCKET_EVENT_INTERACTION_DECLINED_EVENT({ dispatch }, event) {
    const interaction = new EcsInteraction(event);
    dispatch('removeActiveInteraction', interaction);
  },

  /**
   * Fired ONLY for the Agent that is Handling the Interaction.
   * This is the ONLY Agent that should persist historical info.
   * Not for consulted agents.
   *
   * INTERACTION_REMOVED_FROM_AGENT just should be used to close the window.
   */
  SOCKET_EVENT_INTERACTION_ENDED_EVENT({ dispatch }, event) {
    dispatch(
      'EVENT_TIMING/addEventTimingConstraintForKPICounter',
      {
        counterTypeId: agentSubscriptions.busyTime.counterTypeId,
        handlerId: event.agentId
      },
      { root: true }
    );
  },

  SOCKET_EVENT_INTERACTION_ENTERED_QUEUE_EVENT({ dispatch }, event) {
    // Note: This may be only for Queue level event monitoring (dashboard/stats)
  },

  SOCKET_EVENT_INTERACTION_OVERFLOWED_EVENT({ dispatch }, event) {
    // Note: This may be only for Queue level event monitoring (dashboard/stats)
  },

  SOCKET_EVENT_INTERACTION_POSTPONED_EVENT({ dispatch, commit, rootGetters }, { interactionId }) {
    removeAfterHandling[interactionId] = true;
    dispatch('ELEMENTS/removeClosedElementsForInteraction', { interactionId }, { root: true });
    commit('deleteHistoricalInteraction', interactionId);
    postponedInteractions[interactionId] = true;
  },

  SOCKET_EVENT_INTERACTION_RECORDING_BY_AGENT_STARTED_EVENT({ dispatch, rootGetters }, event) {
    const elements = rootGetters['ELEMENTS/getElementsForInteraction'](event);

    elements.forEach(element => {
      if (!element.recording) {
        return;
      }

      element.recording = { isEnabled: true, isPaused: false };
      dispatch('ELEMENTS/editElement', element, { root: true });
    });
  },

  SOCKET_EVENT_INTERACTION_RECORDING_BY_AGENT_STOPPED_EVENT({ dispatch, rootGetters }, event) {
    const elements = rootGetters['ELEMENTS/getElementsForInteraction'](event);

    elements.forEach(element => {
      if (!element.recording) {
        return;
      }

      element.recording = { isEnabled: false, isPaused: false };
      dispatch('ELEMENTS/editElement', element, { root: true });
    });
  },

  SOCKET_EVENT_INTERACTION_RECORDING_PAUSED_EVENT({ dispatch, rootGetters }, event) {
    const elements = rootGetters['ELEMENTS/getElementsForInteraction'](event);

    elements.forEach(element => {
      if (!element.recording) {
        return;
      }

      element.recording = { isEnabled: true, isPaused: true };
      dispatch('ELEMENTS/editElement', element, { root: true });
    });
  },

  SOCKET_EVENT_INTERACTION_RECORDING_RESUMED_EVENT({ dispatch, rootGetters }, event) {
    const elements = rootGetters['ELEMENTS/getElementsForInteraction'](event);

    elements.forEach(element => {
      if (!element.recording) {
        return;
      }

      element.recording = { isEnabled: true, isPaused: false };
      dispatch('ELEMENTS/editElement', element, { root: true });
    });
  },

  SOCKET_EVENT_INTERACTION_REMOVED_FROM_AGENT_EVENT({ dispatch, commit, getters, rootGetters }, event) {
    const interaction = new EcsInteraction(event);

    if (
      [
        INTERACTION_STATE.offered,
        INTERACTION_STATE.missed,
        INTERACTION_STATE.rejected,
        INTERACTION_STATE.canceled
      ].includes(interaction.interactionStateId)
    ) {
      return dispatch('removePendingInteraction', interaction);
    } else if (
      [INTERACTION_STATE.handled, INTERACTION_STATE.delegatedToSupervisor].includes(interaction.interactionStateId)
    ) {
      const activeInteraction = getters.getActiveInteraction(event.interactionId);
      const elements = rootGetters['ELEMENTS/getElementsForInteraction'](event.interactionId);
      dispatch('removePendingInteraction', interaction);

      if (!elements.length || (activeInteraction && activeInteraction.isConsultReceiver)) {
        return dispatch('removeActiveInteraction', interaction);
      }

      return dispatch('moveActiveInteractionToHistory', interaction);
    } else {
      return null;
    }
  },

  SOCKET_EVENT_INTERACTION_STATE_CHANGED_EVENT({ dispatch }, event) {
    const interaction = new EcsInteraction(event);
    dispatch('editActiveInteraction', interaction);
  },

  SOCKET_EVENT_INTERACTION_TRANSFERRED_EVENT({ dispatch }, event) {},

  SOCKET_EVENT_CONTACT_LINKED_TO_INTERACTION_EVENT({ dispatch, getters, commit }, event) {
    const { interactionId, contactId } = event;

    const interaction = getters.getInteraction(interactionId);

    if (interaction) {
      // 1. Request customer
      dispatch('CONTACTS/requestCrmContact', contactId, { root: true });

      // 2. Link customer to interaction
      dispatch('updateInteractionCrmContactId', { interactionId, contactId });
    }
  }
};

const interactionsHistory = {
  loadedDays: 0
};

const stateActions = {
  upsertPendingInteraction: ({ commit, getters, dispatch }, interaction) => {
    if (interaction.interactionId) {
      const pendingInteraction = getters.getPendingInteraction(interaction.interactionId);

      if (interaction.callbackRecordingFile || interaction.callbackRecordingTranscription) {
        const recording = {
          interactionId: interaction.interactionId,
          callbackRecordingFile: interaction.callbackRecordingFile,
          callbackRecordingTranscription: interaction.callbackRecordingTranscription,
          previewTime: interaction.previewTime
        };
        dispatch('INTERACTIONS/recordings/storeRecording', recording, { root: true });
      }

      if (pendingInteraction) {
        let timestamp;

        if (!interaction.timestamp) {
          timestamp = pendingInteraction.timestamp;
        } else {
          timestamp = interaction.timestamp;
        }

        const interactionToStore = {
          ...interaction,
          consult: pendingInteraction.consult,
          timestamp
        };
        commit('updatePendingInteraction', interactionToStore);
      } else {
        commit('createPendingInteraction', interaction);
      }
    }
  },

  movePendingInteractionToActive: ({ commit, dispatch, getters }, interaction) => {
    if (interaction.interactionId) {
      const pendingInteraction = getters.getPendingInteraction(interaction.interactionId);

      const consult = pendingInteraction ? pendingInteraction.consult : null;
      if (consult) {
        interaction.consult = consult;
      }

      const completionStatus = pendingInteraction ? pendingInteraction.completionStatus : null;
      if (completionStatus) {
        interaction.completionStatus = completionStatus;
      }

      // CallbackRecordingFile and callbackRecordingTranscription are only available on offered state.
      // Both properties should be persisted in order to use this values later.
      if (!interaction.callbackRecordingFile && pendingInteraction) {
        interaction.callbackRecordingFile = pendingInteraction.callbackRecordingFile;
      }

      if (!interaction.callbackRecordingTranscription && pendingInteraction) {
        interaction.callbackRecordingTranscription = pendingInteraction.callbackRecordingTranscription;
      }

      commit('deletePendingInteraction', interaction.interactionId);
      if (interaction.isTelephony) {
        dispatch('removeAllActiveTelephonyInteractions');
      }
      dispatch('addActiveInteraction', interaction);
    }
  },

  removePendingInteraction: ({ commit }, interaction) => {
    if (interaction.interactionId) {
      commit('deletePendingInteraction', interaction.interactionId);
    }
  },

  removeAllActiveTelephonyInteractions: ({ dispatch, getters }) => {
    // Only 1 active telephony interaction is allowed at a time. 04.2025
    const telephonyInteractions = getters.getActiveTelephonyInteractions;
    telephonyInteractions.forEach(interaction => {
      InteractionService.endInteraction(interaction.interactionId);
    });
  },

  addActiveInteraction: ({ commit, dispatch, rootGetters }, newInteraction) => {
    if (newInteraction.interactionId) {
      const interaction = new EcsInteraction(newInteraction);

      if (interaction.crm.contactId) {
        dispatch('CONTACTS/requestCrmContact', interaction.crm.contactId, { root: true });
      }

      commit('createActiveInteraction', interaction);

      const existingWindow = rootGetters['WINDOWS/getInteractionWindow'](interaction.interactionId);
      if (!existingWindow) {
        dispatch('WINDOWS/createInteractionWindow', interaction.interactionId, { root: true });
      } else {
        dispatch('WINDOWS/restoreInteractionWindow', interaction.interactionId, { root: true });
      }
    }
  },

  editActiveInteraction: ({ commit, dispatch, getters }, interaction) => {
    if (!interaction.interactionId) {
      return;
    }

    const activeInteraction = getters.getActiveInteraction(interaction.interactionId);

    if (!activeInteraction) {
      const pendingInteraction = getters.getPendingInteraction(interaction.interactionId);
      if (pendingInteraction) {
        if (!interaction.callbackRecordingFile) {
          interaction.callbackRecordingFile = pendingInteraction.callbackRecordingFile;
          interaction.callbackRecordingTranscription = pendingInteraction.callbackRecordingTranscription;
          // Uncomment this section to test on dev environment.
          // if (interaction.callbackRecordingFile.startsWith('https')) {
          //   interaction.callbackRecordingFile = interaction.callbackRecordingFile.replace(/^htpps/i, 'http');
          // }
        }
        if (!interaction.completionStatuses) {
          interaction.completionStatuses = pendingInteraction.completionStatuses;
        }

        return dispatch('upsertPendingInteraction', interaction);
      }
    }

    if (!interaction.callbackRecordingFile && activeInteraction) {
      interaction.callbackRecordingFile = activeInteraction.callbackRecordingFile;
    }

    if (!interaction.callbackRecordingTranscription && activeInteraction) {
      interaction.callbackRecordingTranscription = activeInteraction.callbackRecordingTranscription;
    }

    if (interaction.connectedPartiesListNew) {
      interaction.connectedPartiesList = structuredClone(interaction.connectedPartiesListNew);
    }

    // WORKAROUND NOTE: This will save the selected completionStatusId and completionStatusName
    // since ECS events or API (interaction/all) are not returning these.
    // Also adding the selected option to completion status for historical since this array changes
    // during the interaction lifecycle and we could lose the selected option like DELEGATE TO SUPERVISOR
    if (
      activeInteraction &&
      activeInteraction.completionStatus &&
      activeInteraction.completionStatus.completionStatusId
    ) {
      interaction.completionStatus = activeInteraction.completionStatus;

      const { completionStatusId, completionStatusName } = interaction.completionStatus;
      const completionStatusIndex = interaction.completionStatuses.findIndex(cs => cs.value === completionStatusId);
      if (completionStatusIndex === -1) {
        interaction.completionStatuses.push({
          value: completionStatusId,
          name: completionStatusName
        });
      }
    }
    // END OF WORKAROUND

    if (
      activeInteraction &&
      activeInteraction.consult &&
      (activeInteraction.consult.requestor || activeInteraction.consult.receiver)
    ) {
      interaction = { ...interaction, consult: activeInteraction.consult };
    }

    commit('updateActiveInteraction', interaction);
  },

  moveActiveInteractionToHistory: ({ commit, dispatch, getters, rootGetters }, { interactionId }) => {
    if (!interactionId) {
      return;
    }

    const activeInteraction = getters.getActiveInteraction(interactionId);
    if (!activeInteraction) {
      return;
    }

    // 1. Remove notifications
    dispatch('NOTIFICATIONS/removeNotificationsForInteraction', interactionId, { root: true });

    // 2. Close Window
    dispatch('WINDOWS/closeInteractionWindow', interactionId, { root: true });

    // 3. Move active interaction to history if not an outgoing email
    const interaction = new EcsInteraction(activeInteraction);
    if (interaction.disposition.dispositionCodeName === 'Not Selected Timeout') {
      interaction.disposition.dispositionCodeId = NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID;
    }
    if (!interaction.isDirectCall && !(interaction.isInternal && interaction.isChat)) {
      commit('createHistoricalInteraction', interaction);
    }

    // 4. Move elements to history
    const elements = rootGetters['ELEMENTS/getElementsForInteraction'](interactionId);
    elements.forEach(element => {
      commit('ELEMENTS/createHistoricalElement', element, { root: true });
      if (activeInteraction.isChat || activeInteraction.isSms) {
        commit(
          'CONVERSATIONS/createHistoricalConversation',
          rootGetters['CONVERSATIONS/getConversationForElement'](element),
          {
            root: true
          }
        );
      }
    });
    commit('ELEMENTS/deleteElementsForInteraction', interactionId, { root: true });
    commit('CONVERSATIONS/deleteConversationsForInteraction', interactionId, { root: true });

    // 5. Email data
    if (interaction.isEmail) {
      dispatch('INTERACTIONS/emails/moveEmailToHistory', { interactionId }, { root: true });
    }

    // 6. Clean up stored data
    dispatch('recordings/deleteRecording', interactionId);
    dispatch('recordings/deleteCallRecordingState', interactionId);

    // 7. Delete active interaction
    commit('deleteActiveInteraction', interactionId);
  },

  removeActiveInteraction: ({ commit, dispatch }, { interactionId }) => {
    if (interactionId) {
      // 1. Remove notifications
      dispatch('NOTIFICATIONS/removeNotificationsForInteraction', interactionId, { root: true });

      // 2. Delete window.
      commit('WINDOWS/deleteInteractionWindow', interactionId, { root: true });

      // 3. Delete elements and conversations.
      commit('ELEMENTS/deleteElementsForInteraction', interactionId, { root: true });
      commit('CONVERSATIONS/deleteConversationsForInteraction', interactionId, { root: true });

      // 4. Delete interaction.
      commit('deleteActiveInteraction', interactionId);

      // 5. Cleanup Helpers
      delete answeredInteractions[interactionId];
    }
  },

  editHistoricalInteraction: ({ commit }, interaction) => {
    if (interaction.interactionId) {
      commit('updateHistoricalInteraction', interaction);
    }
  },

  removeHistoricalInteraction: ({ commit, dispatch }, interaction) => {
    if (interaction.interactionId) {
      commit('deleteHistoricalInteraction', interaction.interactionId);
      commit('ELEMENTS/deleteHistoricalElementsForInteraction', interaction.interactionId, { root: true });
      commit('CONVERSATIONS/deleteHistoricalConversationsForInteraction', interaction.interactionId, { root: true });
      if (interaction.isEmail) {
        dispatch('INTERACTIONS/emails/deleteHistoricalEmailsForInteraction', interaction.interactionId, { root: true });
      }
      commit('WINDOWS/deleteInteractionWindow', interaction.interactionId, { root: true });
    }
  },

  // NOTE: Workaround to solve the bad practices in the ECS RESTful API services :(
  updateInteractionDispositionCode: ({ commit }, data) => {
    if (data.interactionId) {
      commit('updateInteractionDispositionCode', data);
    }
  },

  updateInteractionCrmContactId: ({ commit, getters, rootGetters }, data) => {
    const { interactionId, contactId } = data;

    const interaction = getters.getInteraction(interactionId);

    if (!interaction) {
      return;
    }

    // 1. Update crm.contactId of the customer
    commit('updateInteractionCrmContactId', data);

    // 2. Update receptor.id of the main element of the interaction
    const mainElement = rootGetters['ELEMENTS/getElementsForInteraction'](interactionId).shift();
    mainElement.receptor.id = contactId;
    commit('ELEMENTS/updateElement', mainElement, { root: true });

    // 3. Update originator.id for the past conversation
    if (interaction.isChat) {
      const conversation = rootGetters['CONVERSATIONS/getConversationForElement'](mainElement);
      const messages = conversation.messages.map(message => {
        const result = message;
        if (message.originator.name === interaction.crm.customerFullName) {
          result.originator.id = contactId;
        }
        return result;
      });

      commit('CONVERSATIONS/setMessagesToConversation', { elementId: mainElement.elementId, messages }, { root: true });
    }
  },

  updateInteractionCompletionStatus: ({ commit }, data) => {
    if (data.interactionId) {
      commit('updateInteractionCompletionStatus', data);
    }
  },

  updateInteractionCompletionStatusInformation: ({ commit }, data) => {
    if (data.interactionId) {
      commit('updateInteractionCompletionStatusInformation', data);
    }
  },

  setInterationDestinationForEmail: ({ commit }, payload) => {
    commit('setInterationDestinationForEmail', payload);
  },

  createInteractionCrmContact: async ({ dispatch }, interaction) => {
    const contactData = interaction.isTelephony
      ? { phone: interaction.routing.origin, email: null }
      : { phone: null, email: interaction.customerEmail };

    const contactCreated = await ContactService.createCrmContact(contactData).catch(error => {
      throw error;
    });

    if (!contactCreated || !contactCreated.data || !contactCreated.data.contactId) {
      throw new Error('Invalid contactId returned by the API');
    }

    const contactId = contactCreated.data.contactId;
    await dispatch('assignInteractionCrmContact', { interaction, contactId });

    return {
      id: contactId,
      firstName: interaction.customer.name
    };
  },

  requestAgentInteractionHistory(
    { dispatch, commit, getters, rootGetters },
    { latestInteractions } = { latestInteractions: false }
  ) {
    const maxHistoryPeriod = rootGetters['SETTINGS/currentUserSettings'].preferencesSettings.interactionHistoryPeriod;
    const maxDays = Math.round(maxHistoryPeriod / 24) || 3;

    if (interactionsHistory.loadedDays === maxDays) {
      return Promise.resolve({ maxLimit: true });
    }

    const startTime = moment()
      .subtract(latestInteractions ? interactionsHistory.loadedDays - 1 : interactionsHistory.loadedDays, 'day')
      .startOf('day');
    const endTime = moment()
      .subtract(latestInteractions ? 0 : interactionsHistory.loadedDays, 'day')
      .endOf('day');

    const filters = {
      agentId: rootGetters['AGENT/loggedInAgent'].id,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString()
    };

    return ContactService.requestAgentInteractionHistory(filters).then(({ data: { agentInteractions } }) => {
      const filteredInteractions = agentInteractions.filter(
        interaction => !(interaction.interactionType.toUpperCase() === OUTGOING_EMAIL && !interaction.destination)
      );

      const sortedInteractions = structuredClone(filteredInteractions).sort((a, b) => {
        return moment(a.startTime).isBefore(moment(b.startTime)) ? 1 : -1;
      });

      if (latestInteractions) {
        commit('setAgentInteractionHistory', {
          agent: rootGetters['AGENT/loggedInAgent'],
          businessProcesses: rootGetters['BUSINESS_PROCESSES/availableBusinessProcesses'],
          agentInteractions: sortedInteractions || []
        });
      } else {
        commit('addAgentInteractionsToHistory', {
          agent: rootGetters['AGENT/loggedInAgent'],
          businessProcesses: rootGetters['BUSINESS_PROCESSES/availableBusinessProcesses'],
          agentInteractions: sortedInteractions || []
        });
        ++interactionsHistory.loadedDays;
      }

      const contacts = [...new Set(sortedInteractions.map(({ contactId }) => contactId))];
      dispatch('CONTACTS/requestCrmContactBatch', contacts, { root: true });
      commit('setHistorySince', startTime);
    });
  },

  assignInteractionCrmContact: async ({ getters, dispatch, rootGetters }, { interaction, contactId }) => {
    await InteractionService.setCrmContact(interaction.interactionId, contactId).catch(error => {
      throw error;
    });

    if (rootGetters['CONTACTS/getCrmContact'](contactId)) {
      const updateParameters = interaction.isTelephony
        ? { contactId, properties: { phone1: interaction.routing.origin } }
        : { contactId, properties: { email: interaction.customerEmail } };
      dispatch('CONTACTS/updateCrmContact', updateParameters, { root: true });
    } else {
      const contact = await dispatch('CONTACTS/requestCrmContact', contactId, { root: true });
      if (interaction.isTelephony && contact.phone1 !== interaction.routing.origin) {
        await dispatch(
          'CONTACTS/updateCrmContact',
          { contactId, properties: { phone1: interaction.routing.origin } },
          { root: true }
        );
      } else if (contact.email !== interaction.customerEmail) {
        await dispatch(
          'CONTACTS/updateCrmContact',
          { contactId, properties: { email: interaction.customerEmail } },
          { root: true }
        );
      }
    }

    dispatch('updateInteractionCrmContactId', { interactionId: interaction.interactionId, contactId });

    return true;
  },

  // WORKAROUND NOTE: This will add a new interaction history for external agent that
  // we can see in history crm contacts only for sms
  addAgentInteractionHistory({ commit, getters, dispatch, rootGetters }, agentInteraction) {
    if (!getters.getInteraction(agentInteraction.interactionId)) {
      commit('addAgentInteractionHistory', {
        agent: rootGetters['AGENT/loggedInAgent'],
        businessProcesses: rootGetters['BUSINESS_PROCESSES/availableBusinessProcesses'],
        agentInteraction
      });
      if (!rootGetters['ELEMENTS/getElementsForInteraction'](agentInteraction.interactionId).length) {
        const newElement = new EcsElement({
          elementId: uuidv1(),
          interactionId: agentInteraction.interactionId,
          elementStateId: ELEMENT_STATE.closed,
          elementStateName: 'Closed',
          entryTime: agentInteraction.startTime,
          waitingTime: agentInteraction.waitTime,
          timestamp: agentInteraction.endTime,
          receptor: {
            email: '',
            id: uuidv1(),
            name: agentInteraction.origin
          },
          origin: agentInteraction.origin
        });
        dispatch('ELEMENTS/addElement', newElement, { root: true });
      }
    }
  }
};

export default {
  ...socketEventActions,
  ...stateActions
};
